import { Box, Button, IconButton, InputAdornment, TextField, Typography, styled } from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { CacheRtl } from "../cache/CacheRtl";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import { useState } from "react";

const FormContainer = styled(Box)({
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
});

const TitleContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginBottom: "10px",
});

const TitleText = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: "34px",
  lineHeight: "42px",
  letterSpacing: "-0.5px",
  [theme.breakpoints.down("md")]: {
    fontSize: "26px",
    lineHeight: "32px",
  },
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  color: "rgba(255,255,255,0.62)",
  fontWeight: 400,
  fontSize: "15px",
  lineHeight: "23px",
  marginBottom: "32px",
  [theme.breakpoints.down("md")]: {
    fontSize: "13.5px",
    lineHeight: "20px",
    marginBottom: "20px",
  },
}));

const InputWrapper = styled(Box)(({ theme }) => ({
  marginBottom: "18px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    marginBottom: "14px",
  },
}));

const InputLabel = styled(Typography)({
  fontSize: "13px",
  fontWeight: 600,
  color: "rgba(255,255,255,0.78)",
  marginBottom: "8px",
  letterSpacing: "0.2px",
});

const StyledTextField = styled(TextField)({
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  "& .MuiOutlinedInput-root": {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "12px",
    backgroundColor: "rgba(255,255,255,0.04)",
    transition: "all 0.2s ease",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.12)",
      transition: "all 0.2s ease",
    },
    "&:hover fieldset": {
      borderColor: "rgba(1,187,167,0.55)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.06)",
      boxShadow: "0 0 0 4px rgba(1,187,167,0.14)",
      "& fieldset": {
        borderColor: "#01BBA7",
        borderWidth: "1.5px",
      },
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px 16px",
    fontSize: "15px",
    color: "#FFFFFF",
    "&::placeholder": {
      color: "rgba(255,255,255,0.38)",
      opacity: 1,
    },
    // keep autofill from painting a white box over the dark input
    "&:-webkit-autofill": {
      WebkitTextFillColor: "#FFFFFF",
      WebkitBoxShadow: "0 0 0 1000px rgba(20,26,38,0.9) inset",
      caretColor: "#FFFFFF",
      transition: "background-color 9999s ease-in-out 0s",
    },
  },
});

const ErrorText = styled(Typography)({
  color: "#FF9B9D",
  fontSize: "13.5px",
  fontWeight: 500,
  marginTop: "4px",
  marginBottom: "4px",
  padding: "11px 14px",
  backgroundColor: "rgba(240, 96, 99, 0.12)",
  borderRadius: "10px",
  border: "1px solid rgba(240, 96, 99, 0.3)",
});

const ForgotPasswordLink = styled(Typography)({
  cursor: "pointer",
  color: "rgba(255,255,255,0.7)",
  fontWeight: 500,
  fontSize: "13.5px",
  marginTop: "14px",
  alignSelf: "flex-start",
  transition: "all 0.2s ease",
  "&:hover": {
    color: "#01BBA7",
  },
});

const SubmitButton = styled(Button)({
  width: "100%",
  marginTop: "24px",
  padding: "15px 24px",
  borderRadius: "12px",
  fontSize: "16px",
  fontWeight: 700,
  letterSpacing: "0.3px",
  textTransform: "none",
  color: "#04231F",
  background: "linear-gradient(135deg, #3FD3C2 0%, #01BBA7 100%)",
  boxShadow: "0 8px 24px rgba(1,187,167,0.35)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #01BBA7 0%, #019385 100%)",
    boxShadow: "0 10px 30px rgba(1,187,167,0.45)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  "&.Mui-disabled": {
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.32)",
    boxShadow: "none",
  },
});

const VisibilityButton = styled(IconButton)({
  color: "rgba(255,255,255,0.5)",
  transition: "all 0.2s ease",
  "&:hover": {
    color: "#01BBA7",
    backgroundColor: "rgba(1,187,167,0.1)",
  },
});

const LoginForm = ({ handleRestLogin, openForgottenPassword, failedLogin }) => {
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <CacheProvider value={CacheRtl}>
      <FormContainer component="form">
        <TitleContainer>
          <TitleText sx={{ color: "#FFFFFF" }}>התחברות ל</TitleText>
          <TitleText sx={{ color: "#3FD3C2" }}>PROGRESS</TitleText>
        </TitleContainer>

        <SubtitleText>
          התחילו לבנות את תוכניות האימון של המתאמנים שלכם בצורה המקצועית ביותר!
        </SubtitleText>

        <InputWrapper>
          <InputLabel>מייל</InputLabel>
          <StyledTextField
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="example@gmail.com"
            id="email"
            variant="outlined"
            autoComplete="email"
          />
        </InputWrapper>

        <InputWrapper sx={{ marginBottom: 0 }}>
          <InputLabel>סיסמה</InputLabel>
          <StyledTextField
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="הזינו את הסיסמה שלכם"
            type={showPassword ? 'text' : 'password'}
            id="password"
            variant="outlined"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <VisibilityButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="start"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </VisibilityButton>
                </InputAdornment>
              ),
            }}
          />
        </InputWrapper>

        {failedLogin && (
          <ErrorText sx={{ marginTop: "16px" }}>
            שם משתמש או הסיסמה אינם תואמים, אנא נסו שנית
          </ErrorText>
        )}

        <ForgotPasswordLink onClick={openForgottenPassword}>
          שכחת סיסמה?
        </ForgotPasswordLink>

        <SubmitButton
          disabled={password.length < 1 || email.length < 1}
          type="submit"
          variant="contained"
          onClick={handleRestLogin}
        >
          התחברות
        </SubmitButton>
      </FormContainer>
    </CacheProvider>
  );
};

export default LoginForm;
